import { Box, CircularProgress, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Fragment, useEffect, useState } from 'react';
import {
    Show, TextField, TabbedShowLayout, Tab,
    Datagrid, ReferenceField, ReferenceManyField,
    DeleteWithConfirmButton, useTranslate, FunctionField,
    useShowController, TopToolbar, ReferenceArrayField, EditButton,
    CreateButton, List, usePermissions, useRecordContext,
    BulkDeleteWithConfirmButton,
}
    from 'react-admin';
import BasicBreadcrumbs from '../../components/breadcrumb';
import AssociateUserToPod from '../pods/associatePodToUser';
import DisAssociateUserToPod from '../pods/disassociatePodToUser';
import AssociateTrainerToSession from '../sessions/associateTrainerToSession';
import DisassociateTrainer from '../sessions/disassociateTrainer';
import BecomeUser from '../users/becomeUser';
import DeployNewPodUser from './deployNewPodUser';
import DeployCommonResources from './deployCommonResources';
import DeployUserResources from './deployUserResources';
import GlobalAssociateUserToPod from '../pods/globalAssociatePodToTrainees';
import Button from '@mui/material/Button';
import { Visibility } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import ClearClassroom from './clearClassroom';
import { ResetSelection, useClearSelectionsOnChange } from '../../components';
import DeployTrainingTemplate from './deployTrainingTemplate';
import axios from 'axios';
import { CustomBulkDeleteDialog } from '../../custom';
import AssociateRapToPod from '../remoteAccessPoint/associateRapToPod';
import { Stack, Typography } from "@mui/material";
import DisAssociateRapToPod from '../remoteAccessPoint/disassociateRapToPod';

const useStyles = makeStyles((theme) => ({
    tableLastCell: {
        width: '50%',
        textAlign: 'right'
    },
    tableLastCellBis: {
        width: '100%',
        textAlign: 'right'
    },
    endCell: {
        width: '10%',
        marginLeft: '85%',
    },
    descriptionCell: {
        width: '20%'
    },
    nameCell: {
        whiteSpace: 'nowrap',
    },
    topToolbar: {
        minHeight: 2
    },
    lowToolbar: {
        minHeight: 2,
        padding: 0
    },
    button: {
        //     minWidth: '10px',
        //     width: '36px',
        //     height: '32px'
    },
    nameLinkCell: {
        width: '150px',  // Adjust width for name column
        maxWidth: '150px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        font: 'inherit',
        color: theme.palette.primary.main,
        textDecoration: 'none'
    },
    nbOfVmsCell: {
        width: '15%',
        whiteSpace: 'nowrap',
    }
}));


const Pill = ({ value, bg }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            minWidth: 24,
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            textAlign: 'center',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
            bgcolor: bg,
        }}
    >
        {value}
    </Box>
);


const RapSerialNumberField = () => {
    const record = useRecordContext();

    if (!record) return null;

    const serial = record.rapSerialNumber;
    const ssid = record.rapSSID;

    // Empty value
    if (!serial) {
        return <Typography component="span">-</Typography>;
    }

    return (
        <Stack spacing={0.5}>
            {/* Line 1: Serial + action */}
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography component="span">{serial || '-'}</Typography>
                {serial ? <DisAssociateRapToPod pod={record.id} /> : null}
            </Stack>

            {/* Line 2: SSID */}
            <Typography
                component="span"
                sx={{
                    whiteSpace: 'nowrap',
                }}
            >
                <span
                    style={{
                        fontWeight: 600,
                        fontFamily: 'monospace',
                    }}
                >
                    SSID:
                </span>{' '}
                {ssid || '-'}
            </Typography>

        </Stack>
    );
};

// Shared hook to fetch once
const useVmCounts = (podId) => {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!podId) return;
        let alive = true;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(`/api/v1/pods/${podId}/vmCounts`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (alive) setData(data);
            } catch {
                if (alive) setData({ nbVms: 0, nbRunningVms: 0 });
            }
        })();
        return () => { alive = false; };
    }, [podId]);

    return data;
};

// Field for Running
const NbRunningField = () => {
    const record = useRecordContext();
    const counts = useVmCounts(record?.id);

    if (!counts) return <CircularProgress size={18} />;
    return <Pill value={counts.nbRunningVms} bg="success.main" />;
};

// Field for Total
const NbTotalField = () => {
    const record = useRecordContext();
    const counts = useVmCounts(record?.id);

    if (!counts) return <CircularProgress size={18} />;
    return <Pill value={counts.nbVms} bg="warning.main" />;
};

const PodBulkActionButtons = ({ permissions }) => {
    const isTrainerOrTraineeOnly =
        (permissions.length === 1 && (permissions.includes("trainer") || permissions.includes("trainee"))) ||
        (permissions.length === 2 && permissions.includes("trainer") && permissions.includes("trainee"));

    if (isTrainerOrTraineeOnly) {
        return null; // hide bulk delete
    }

    // Admins/Designers (or others): allow bulk delete
    return (
        <>
            {/* <BulkDeleteWithConfirmButton /> */}
            <CustomBulkDeleteDialog
                getLabel={pod => `${pod.name}`}
                confirmTitleKey="app.pods.delete.confirmTitle"
                confirmMessageKey="app.pods.delete.bulkConfirmMessage"
            />
        </>
    );
};

const ClearOnChange = () => {
    useClearSelectionsOnChange();
    return null;
};


const ClassroomShow = ({ ...props }) => {
    const translate = useTranslate();
    const { permissions, isLoading: isLoadingRights } = usePermissions();
    const { record, isLoading, error } = useShowController({ resource: 'classrooms', id: props.id });
    const CustomEmpty = () => <div>{translate('app.emptyMessage')}</div>;
    const classes = useStyles();
    const fullNameField = choice => `${choice.firstname} ${choice.lastname}`;
    const [showError, setShowError] = useState(false); // State to track error visibility
    const optionRendererTrainingTemplate = choice => `${choice.name} #${choice.language} #${choice.step}`;

    const toggleErrorVisibility = () => {
        setShowError(prevState => !prevState); // Toggle the visibility state
    };

    if (isLoadingRights) return <div>{translate('app.LoadingMessage')}</div>;

    if (isLoading) {
        return <div>{translate('app.LoadingMessage')}</div>;
    };

    if (error) {
        return <div>{translate('app.ErrorMessage')}</div>;
    };

    const isAdminOrDesigner = permissions.includes('admin') || permissions.includes('designer');
    const isTrainer = permissions.includes('trainer');
    const isTraineeOnly = permissions.length === 1 && permissions.includes('trainee');

    const buildPodLink = (record) => {
        if (isAdminOrDesigner) return `/pods/${record.id}/show`;
        if (isTrainer) return `/myclassroom/Pod/${record.id}`;
        if (isTraineeOnly) return `/mypod/instances`;
        return '#';
    };

    //Dedicated RAP classrooms
    const rapRooms = new Set(['C90', 'C91', 'C92', 'C93','C30','C31','C32','C33','C34','C35','C36',]);
    const showRap = rapRooms.has(record?.name);

    // Dedicated Rental classrooms 
    const rentalRooms = new Set(['C50', 'C51', 'C52', 'C53','C54','C55','C56']);
    const showRental = rentalRooms.has(record?.name); // True if we are in a rental Room

    const ClassroomShowActions = () => {
        return (
            <TopToolbar className={classes.topToolbar}>
            </TopToolbar>
        )
    };


    const ClassroomActions = () => {
        const translate = useTranslate();
        const record = useRecordContext();
        const { permissions, isLoading } = usePermissions();

        let title = `${translate('app.classrooms.delete.title')}`;

        if (record && record.name) {
            title = `${translate('app.classrooms.delete.title')} ${record.name}`;
        }

        if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

        if (!permissions) return null;


        if (permissions.includes('admin')) {
            return (
                <TopToolbar className={classes.lowToolbar}>
                    <DeployTrainingTemplate classroomId={record.id} classroomName={record.name} />
                    {/* <EditButton /> */}
                    <ClearClassroom />
                    {/* <DeleteWithConfirmButton confirmContent={translate('app.classrooms.delete.confirmMessage')}
                        confirmTitle={title} /> */}
                </TopToolbar>
            )
        } else {
            return (<ClassroomShowActions />);
        }
    };

    const ListActionButtons = ({ permissions }) => {
        const translate = useTranslate();
        const record = useRecordContext();
        const location = useLocation();

        if (!permissions) return null; // safety

        const title = `${translate('app.pods.delete.title')} ${record.name}`;
        const isAdminOrDesigner = permissions.includes("admin") || permissions.includes("designer");

        return (
            <Fragment>
                <Button
                    size="small"
                    className={classes.button}
                    component={Link}
                    to={`${location.pathname}/${record.id}`}
                    startIcon={<Visibility />}
                >
                    show
                </Button>

                {/* Only show delete for admin/designer */}
                {isAdminOrDesigner && (
                    <DeleteWithConfirmButton
                        confirmContent={translate('app.pods.delete.confirmMessage')}
                        confirmTitle={title}
                    />
                )}
            </Fragment>
        );
    };

    const ListActionButtonsForUserPod = () => {
        const translate = useTranslate();
        const record = useRecordContext();
        const location = useLocation();

        const title = `${translate('app.pods.delete.title')} ${record.name}`;
        if(showRental && !isAdminOrDesigner){
           return (
                <Button size='small' className={classes.button}
                                component={Link}
                                to={`${location.pathname}/${record.id}`}
                                startIcon={<Visibility />}
                            >{translate('app.pods.show')}</Button>
            );
        }   
             return (
                <Fragment>
                    {showRap ?
                        <AssociateRapToPod /> : ''}
                    <AssociateUserToPod />
                    <Button size='small' className={classes.button}
                        component={Link}
                        to={`${location.pathname}/${record.id}`}
                        startIcon={<Visibility />}
                    >{translate('app.pods.show')}</Button>
                    <DeleteWithConfirmButton confirmContent={translate('app.pods.delete.confirmMessage')}
                        confirmTitle={title} redirect={false} />
                </Fragment>
            );
    };

    const UserListActionButtons = () => {
        return (
            <Fragment>
                <BecomeUser />
                <DisAssociateUserToPod />
                <EditButton />
            </Fragment>
        );
    };

    const TrainerListActionButtons = () => {
        const { permissions } = usePermissions();

        const isAdmin = permissions?.includes('admin');
        return (
            <Fragment>
                {isAdmin ? <BecomeUser /> : ''}
            </Fragment>
        );
    };

    const TraineeListActionButtons = () => {
        const translate = useTranslate();
        return (
            <Fragment>
                <CreateButton label={translate('app.users.titles.create')} className={classes.endCell} />
                <GlobalAssociateUserToPod id={record.id} />
            </Fragment>
        );
    };

    // <GlobalAssociateUserToPod id={record.id} />

    const SessionActionButtons = () => {
        return (
            <Fragment>
                <AssociateTrainerToSession />
                <DisassociateTrainer />
                <EditButton />
            </Fragment>
        );
    };

    const UserDeployButtons = () => {
        const { permissions } = usePermissions();

        const isAdmin = permissions?.includes('admin');
        const isTrainer = permissions?.includes('trainer');

        // Admin has priority
        if (isAdmin) {
            return (
                <Grid container style={{ display: "flex", justifyContent: 'right' }}>
                    <Grid item>
                        <DeployUserResources room={record.id} />
                    </Grid>
                    <Grid item alignItems="stretch" style={{ display: "flex" }}>
                        <DeployNewPodUser
                            trainingTemplate={record.training_template}
                            room={record.id}
                            roomNumber={record.number}
                            realPodCount={record.realPodCount}
                        />
                    </Grid>
                </Grid>
            );
        }

        // Trainer only (without admin privileges)
        if (!showRental){
            if (isTrainer) {
                console.log(record, 'record');
                return (
                    <Grid container style={{ display: "flex", justifyContent: 'right' }}>
                        <Grid item alignItems="stretch" style={{ display: "flex" }}>
                            <DeployNewPodUser
                                trainingTemplate={record.training_template}
                                room={record.id}
                                trainerPod={true}
                                roomNumber={record.number}
                                realPodCount={record.realPodCount}
                                trainerPodDeployed={record.trainerPodDeployed}
                            />
                        </Grid>
                    </Grid>
                );
            }
        }
        return <></>;
    };

    return (
        <>
            {props.myclassroom ?
                null : props.showTraining ?
                    null : record ? <BasicBreadcrumbs name={record.name} /> : ''}
            <Show {...props} actions={<ClassroomActions />} >

                <TabbedShowLayout>

                    <Tab label='General'>

                        <TextField source='name' />

                        {!isLoading && record.training_template && permissions?.includes("admin") ?
                            <ReferenceField source='training_template' reference='trainingTemplates' link='show'>
                                <FunctionField render={optionRendererTrainingTemplate} label={translate('app.classrooms.name')} />
                            </ReferenceField>
                            : " "
                        }

                        {record && record.stackError && (
                            <>
                                <Button onClick={toggleErrorVisibility}>
                                    {showError
                                        ? translate('app.vms.hideError')
                                        : translate('app.vms.showError')}
                                </Button>
                                {showError && (
                                    <TextField
                                        source="stackError"
                                        label={translate('app.vms.show.error')}
                                    />
                                )}
                            </>
                        )}

                        {!isLoading && record.training ?

                            // info training Session
                            <ReferenceManyField source='training' reference='sessions' target='_id' id='id' link='list' label={translate('app.sessions.trainingSession')}>
                                <Datagrid bulkActionButtons={false} sx={{
                                    '& .RaDatagrid-tableWrapper': {
                                        width: '100%',
                                    },
                                }}>
                                    <TextField source='name' label={translate('app.sessions.name')} />
                                    <TextField source='locator' label={translate('app.classrooms.locator')} />

                                    <ReferenceArrayField source='trainer' reference='users' cellClassName={classes.nameCell} label={translate('app.classrooms.trainer')} >
                                        <Datagrid bulkActionButtons={false}>
                                            <FunctionField render={fullNameField} />
                                            <TrainerListActionButtons cellClassName={classes.tableLastCell} />
                                        </Datagrid>
                                    </ReferenceArrayField>
                               {/* // If onlyTrainer or (onlyTrainer && rentalClassRoom)  , No sessionsAction buttons */}

                                    {(permissions.includes("admin") || permissions.includes("designer") ) ?  <SessionActionButtons cellClassName={classes.tableLastCell} /> : null}
                                </Datagrid>
                            </ReferenceManyField>
                            : " "
                        }
                        {/* We dont display trainee info for  rental class */}
                        {!isLoading && record.training &&  !showRental ?

                            <List resource='users' actions={<TraineeListActionButtons />} pagination={false} >
                                <ReferenceManyField source='training' reference='sessions' target='_id' id='id' link='list' label='false'>
                                    <Datagrid empty={<CustomEmpty />} bulkActionButtons={false} >
                                        <ReferenceArrayField
                                            label={translate('app.classrooms.trainees')}
                                            source='trainees'
                                            reference="users"
                                            target="classroom"
                                        >
                                            <Datagrid empty={<CustomEmpty />} bulkActionButtons={false} sx={{
                                                '& .RaDatagrid-tableWrapper': {
                                                    width: '60%',
                                                },
                                            }}>
                                                <TextField source="firstname" label={translate('app.classrooms.firstname')} />
                                                <TextField source="lastname" label={translate('app.classrooms.lastname')} />
                                                <TextField source="email" cellClassName={classes.nameCell} label={translate('app.classrooms.email')} />
                                                <ReferenceField source='pod' reference='pods' cellClassName={classes.nameCell} >
                                                    <TextField source='name' />
                                                </ReferenceField>
                                                <UserListActionButtons cellClassName={classes.tableLastCell} />
                                            </Datagrid>
                                        </ReferenceArrayField>
                                    </Datagrid>
                                </ReferenceManyField>
                            </List>

                            : " "
                        }


                    </Tab>
                    <Tab label='Pod' path='Pod'>
                        {/* common pod */}
                        <ReferenceManyField
                            label={<h1>{translate('app.pods.common')}</h1>}
                            perPage={15}
                            reference="pods"
                            target="room"
                            sort={{
                                field: 'number',
                                order: 'ASC'
                            }}
                            filter={{
                                type: "common"
                            }}

                        >
                            <Grid container style={{ display: "flex", justifyContent: 'right' }}>
                                {permissions.includes("admin") || permissions.includes("designer") ?
                                    <Grid item>
                                        <DeployCommonResources room={record.id} />
                                    </Grid>
                                    :
                                    <> </>
                                }
                            </Grid>

                            <ResetSelection />
                            <Datagrid empty={<CustomEmpty />} bulkActionButtons={<PodBulkActionButtons permissions={permissions} />}>
                                <TextField source="number" label={translate('app.classrooms.number')} />
                                <FunctionField
                                    label={translate('app.pods.name')}
                                    sortBy="name"
                                    render={record => (
                                        <Link to={buildPodLink(record)} className={classes.nameLinkCell}>
                                            {record.name}
                                        </Link>
                                    )}
                                />
                                <TextField source="description" cellClassName={classes.descriptionCell} label={translate('app.classrooms.description')} />

                                <NbRunningField cellClassName={classes.nbOfVmsCell} label={translate('app.classrooms.numberOfVMsRunning')} />
                                <NbTotalField cellClassName={classes.nbOfVmsCell} label={translate('app.classrooms.numberOfVMs')} />
                                <ListActionButtons permissions={permissions} cellClassName={classes.tableLastCell} />
                            </Datagrid>

                        </ReferenceManyField>
                          {/* User pod */}
                        <ReferenceManyField
                            label={<h1>{translate('app.pods.user')}</h1>}
                            perPage={15}
                            reference="pods"
                            target="room"
                            sort={{
                                field: 'number',
                                order: 'ASC'
                            }}
                            filter={{
                                type: "user"
                            }}
                        >
                            <UserDeployButtons />
                            <ResetSelection />
                            <Datagrid empty={<CustomEmpty />} bulkActionButtons={<PodBulkActionButtons permissions={permissions} />}>
                                <TextField source="number" label={translate('app.classrooms.number')} />
                                <FunctionField
                                    label={translate('app.pods.name')}
                                    sortBy="name"
                                    render={record => (
                                        <Link to={buildPodLink(record)} className={classes.nameLinkCell}>
                                            {record.name}
                                        </Link>
                                    )}
                                />
                                <TextField source="description" cellClassName={classes.descriptionCell} label={translate('app.classrooms.description')} />
                                {!showRental ? <ReferenceManyField
                                    label={translate('app.classrooms.trainees')}
                                    reference="users"
                                    target="pod"
                                    cellClassName={classes.descriptionCell}
                                >
                                    <Datagrid empty={<CustomEmpty />} bulkActionButtons={false}>
                                        <TextField source="firstname" label={translate('app.classrooms.firstname')} />
                                        <TextField source="lastname" label={translate('app.classrooms.lastname')} />
                                        <BecomeUser />
                                        <DisAssociateUserToPod cellClassName={classes.tableLastCell} />
                                    </Datagrid>
                                </ReferenceManyField> : '' }
                                {showRap ?
                                    <RapSerialNumberField pod={record.id} label={translate('app.pods.rapSerialNumber')} />
                                    :
                                    ' '
                                }
                                <NbRunningField cellClassName={classes.nbOfVmsCell} label={translate('app.classrooms.numberOfVMsRunning')} />
                                <NbTotalField cellClassName={classes.nbOfVmsCell} label={translate('app.classrooms.numberOfVMs')} />
                                <ListActionButtonsForUserPod cellClassName={classes.tableLastCellBis} />
                            </Datagrid>
                        </ReferenceManyField>
                    </Tab>
                </TabbedShowLayout>
            </Show >
        </>
    );
};

export default ClassroomShow;